import { AnimationContext, GsapTween } from "./index.types"
import { THREETween } from "./THREETween"
import { Timeline } from "./Timeline";

/** THREE-SAP
 * Creates a shared context for gsap and three.js with shared methods, and makes it easier to work with both animations as a 'group'. 
 * Wraps gsap context and AnimationAction from three to behave as one grouped. 
 * @see https://gsap.com/docs/v3/GSAP/gsap.context()/
 * @see https://threejs.org/docs/#AnimationAction
 * @see https://github.com/greensock/GSAP/blob/master/src/gsap-core.js#2883 
 */

  export class Context implements AnimationContext {
    public tweens: Set<GsapTween | THREETween>;
    public timelines: Set<Timeline>;
    public id: string; 
    constructor(
      tweens: (GsapTween | THREETween) [] = [],
      timelinesOrId?: Timeline | Timeline[] | string,
      id?: string
    ) {
      
      const timelines = Array.isArray(timelinesOrId) ? timelinesOrId : timelinesOrId instanceof Timeline ? [timelinesOrId] : []; //prettier-ignore
      const resolvedId = typeof timelinesOrId === "string" ? timelinesOrId : id;

      this.tweens = new Set(tweens);
      this.timelines = new Set(timelines);
      this.id = resolvedId ? resolvedId : crypto.randomUUID()
    }

    // playing
    play = () => {
      this.tweens.forEach((t) => t.play());
      return this;
    }

    pause = () => {
      this.tweens.forEach((t) => t.pause());
      return this;
    }

    playPause = (time?: number) => {
      this.tweens.forEach((t) => t.playPause(time));
      return this;
    }

    resume = () => {
      this.tweens.forEach((t) => t.resume());
      return this;
    }

    repeat = (n: number) => {
      this.tweens.forEach((t) => t.repeat(n));
      return this;
    }

    eventCallback = (type: "onStart" | "onUpdate" | "onComplete", callback: () => void) => {
      this.tweens.forEach((t) => t.eventCallback(type, callback));
    }

    // killing || resetting
    revert = () => {
      this.tweens.forEach((t) => t.revert());
      return this;
    }

    kill = () => {
      this.tweens.forEach((t) => t.kill());
      return this;
    }

    progress = (value?: number): number => {
      if (this.tweens.size === 0) return 0;

      // The longest tween is by default the overall timeline -> Since play forces all tweens to be located at the start, they all represent duration from the start till the end, not midway till the end or elsewhere
      const longestTween = Array.from(this.tweens).reduce<GsapTween | THREETween | null>((max, tween) => !max || tween.duration() > max.duration() ? tween : max, null);

      if (!longestTween) return 0;
      if (value === undefined) return longestTween.progress()

      const longestDuration = longestTween.duration();

      // Set progress on each tween proportionally
      this.tweens.forEach((tween) => {
        const elapsedTime = longestDuration * value;         // Calculate how much time has elapsed (in seconds)

        // Calculate this tween's progress as a proportion of its own duration
        // Cap at 1.0 so shorter tweens don't overshoot
        const tweenProgress = Math.min(elapsedTime / tween.duration(), 1.0);

        tween.progress(tweenProgress);
      });

      longestTween.progress(value);
      return value;
    }

    duration = () => this.tweens.size ? Math.max(...Array.from(this.tweens, (t) => t.duration())) : 0; //prettier-ignore

    // checks
    // sets prohibit duplicates, but they dont clean the animation therefore we risk having stale animations running if we keep adding them and they're there already.
    add = (t: GsapTween | THREETween | Timeline) => {
      if (t instanceof Timeline) {
        if (this.timelines.has(t)) return;
        this.timelines.add(t);
        return;
      }

      if (this.tweens.has(t)) return;
      this.tweens.add(t);
    }

    delete = (t: GsapTween | THREETween | Timeline) => {
      if (t instanceof Timeline) {
        if (!this.timelines.has(t)) return;
        t.revert();
        this.timelines.delete(t);
        return;
      }

      if (!this.tweens.has(t)) return;
      t.revert();
      this.tweens.delete(t);
    }

    clear = () => {
      this.timelines.clear()
      this.tweens.clear()
    }

    reset() {

     //t.reset is not a function
        this.tweens.forEach((t) => {
          t.reset()
          t.pause()
        })
        return this; 
    }

    // cleaning
    cleanup = () => {
      this.revert()
      this.clear()
    }

    get = (id: string): GsapTween | THREETween | undefined => {
        return Array.from(this.tweens).find((t) => t.id === id)
    }

    then = (callback?: () => void): Promise<void> => {
      const tweens = Array.from(this.tweens);
      const last = tweens[tweens.length - 1];

      if (!last) {
        callback?.();
        return Promise.resolve();
      }

      return last.then(() => {}).then(() => {callback?.()});
    };
  }
